import React, { useEffect, useRef } from 'react'

const SNOWFLAKE_COUNT = 80
const MOUSE_INFLUENCE = 0.06 // Force d'influence de la souris

const Snowfall = ({ enabled = true }) => {
  const canvasRef = useRef(null)
  const snowflakesRef = useRef([])
  const mouseRef = useRef({ x: -1000, y: -1000, active: false })
  const animationRef = useRef(null)

  useEffect(() => {
    if (!enabled) return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    // Resize canvas
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Track mouse
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true }
    }
    const handleMouseLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false }
    }
    const handleMouseEnter = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true }
    }
    const handleBlur = () => {
      mouseRef.current = { ...mouseRef.current, active: false }
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.body.addEventListener('mouseleave', handleMouseLeave)
    document.body.addEventListener('mouseenter', handleMouseEnter)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('mouseout', (e) => {
      // Vérifie si la souris a vraiment quitté la fenêtre
      if (!e.relatedTarget && !e.toElement) {
        mouseRef.current = { ...mouseRef.current, active: false }
      }
    })

    // Crée un flocon avec position aléatoire sur TOUTE la page (pré-rempli)
    const createSnowflake = (randomY = true) => ({
      x: Math.random() * canvas.width,
      y: randomY ? Math.random() * canvas.height : -10,
      radius: 1.5 + Math.random() * 3,
      opacity: 0.4 + Math.random() * 0.5,
      speedY: 0.3 + Math.random() * 0.7,
      speedX: 0,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.01 + Math.random() * 0.02,
    })

    // Initialise les flocons - PARTOUT sur la page dès le départ
    snowflakesRef.current = Array.from({ length: SNOWFLAKE_COUNT }, () => createSnowflake(true))

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      snowflakesRef.current.forEach((flake, i) => {
        // Wobble horizontal naturel
        flake.wobble += flake.wobbleSpeed
        const wobbleX = Math.sin(flake.wobble) * 0.3

        // Direction vers la souris - SEULEMENT si la souris est dans la page
        if (mouseRef.current.active) {
          const dx = mouseRef.current.x - flake.x
          const targetSpeedX = (dx / canvas.width) * 2
          flake.speedX += (targetSpeedX - flake.speedX) * MOUSE_INFLUENCE
        } else {
          // Pas de souris = retour progressif à 0
          flake.speedX *= 0.95
        }

        // Mouvement
        flake.x += flake.speedX + wobbleX
        flake.y += flake.speedY

        // Reset si sort de l'écran
        if (flake.y > canvas.height + 10) {
          snowflakesRef.current[i] = createSnowflake(false)
        }
        if (flake.x < -10) flake.x = canvas.width + 10
        if (flake.x > canvas.width + 10) flake.x = -10

        // Dessine le flocon
        const gradient = ctx.createRadialGradient(
          flake.x, flake.y, 0,
          flake.x, flake.y, flake.radius
        )
        gradient.addColorStop(0, `rgba(255, 255, 255, ${flake.opacity})`)
        gradient.addColorStop(0.6, `rgba(255, 255, 255, ${flake.opacity * 0.5})`)
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

        ctx.beginPath()
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.fill()
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resize)
      document.removeEventListener('mousemove', handleMouseMove)
      document.body.removeEventListener('mouseleave', handleMouseLeave)
      document.body.removeEventListener('mouseenter', handleMouseEnter)
      window.removeEventListener('blur', handleBlur)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [enabled])

  if (!enabled) return null

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9998,
        pointerEvents: 'none',
      }}
    />
  )
}

export default Snowfall
